import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { formatCurrency } from '../lib/utils';
import {
  Package,
  MapPin,
  Euro,
  FileText,
  CheckCircle2,
  Loader2,
  LogIn,
  Download,
  ExternalLink,
  Shield,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomerPortal = () => {
  const [step, setStep] = useState('login'); // login, order, confirmed
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [order, setOrder] = useState(null);
  
  // Login form
  const [orderNumber, setOrderNumber] = useState('');
  const [accessCode, setAccessCode] = useState('');
  
  // Confirmation checkboxes
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [widerrufAccepted, setWiderrufAccepted] = useState(false);
  const [datenschutzAccepted, setDatenschutzAccepted] = useState(false);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/customer/login`, {
        order_number: parseInt(orderNumber),
        access_code: accessCode.toUpperCase()
      });
      
      setToken(response.data.token);
      
      if (response.data.status === 'confirmed') {
        setStep('confirmed');
        loadOrder(response.data.token);
      } else {
        setStep('order');
        loadOrder(response.data.token);
      }
    } catch (error) {
      toast.error('Ungültige Zugangsdaten. Bitte überprüfen Sie Ihre Eingaben.');
    } finally {
      setLoading(false);
    }
  };
  
  const loadOrder = async (authToken) => {
    try {
      const response = await axios.get(`${API_URL}/customer/order`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setOrder(response.data);
      
      if (response.data.status === 'confirmed') {
        setStep('confirmed');
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellung');
    }
  };
  
  const handleConfirm = async () => {
    if (!agbAccepted || !widerrufAccepted || !datenschutzAccepted) {
      toast.error('Bitte akzeptieren Sie alle erforderlichen Dokumente');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/customer/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Bestellung erfolgreich bestätigt!');
      setStep('confirmed');
      loadOrder(token);
    } catch (error) {
      toast.error('Fehler bei der Bestätigung');
    } finally {
      setLoading(false);
    }
  };
  
  const openDocument = (url) => {
    window.open(url, '_blank');
  };
  
  const downloadVorvertraglicheInfo = () => {
    window.open(`${API_URL}/customer/vorvertragliche-info`, '_blank');
  };

  // Login Screen
  if (step === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-[#F47920] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">Kundenportal</CardTitle>
            <CardDescription>
              Melden Sie sich mit Ihren Zugangsdaten an
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="orderNumber">Bestellnummer</Label>
                <Input
                  id="orderNumber"
                  type="number"
                  placeholder="z.B. 700001"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  required
                  data-testid="order-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accessCode">Zugangscode</Label>
                <Input
                  id="accessCode"
                  type="text"
                  placeholder="z.B. A7K9M2X4"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  className="font-mono tracking-wider uppercase"
                  maxLength={8}
                  required
                  data-testid="access-code-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#F47920] hover:bg-[#E06810]"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <LogIn className="w-4 h-4 mr-2" />
                )}
                Anmelden
              </Button>
            </form>
            <p className="text-xs text-slate-500 text-center mt-4">
              Die Zugangsdaten finden Sie in der E-Mail von nexiu
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Order View or Confirmed View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-[#F47920] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            Bestellung #{order?.order_number}
          </h1>
          <Badge 
            className={`mt-2 ${
              order?.status === 'confirmed' 
                ? 'bg-emerald-100 text-emerald-800' 
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            {order?.status === 'confirmed' ? 'Bestätigt' : 'Wartet auf Bestätigung'}
          </Badge>
        </div>

        {/* Success Message for Confirmed */}
        {step === 'confirmed' && (
          <Card className="border-0 shadow-lg bg-emerald-50 border-emerald-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-emerald-900">Bestellung bestätigt</h3>
                  <p className="text-sm text-emerald-700">
                    Vielen Dank! Wir werden uns in Kürze bei Ihnen melden.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Order Details */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#F47920]" />
              Produktdetails
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Produkt</p>
                <p className="font-semibold">{order?.product_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Vertragslaufzeit</p>
                <p className="font-semibold">
                  {order?.contract_type === '24m' ? '24 Monate' : 'Ohne Mindestlaufzeit'}
                </p>
              </div>
            </div>
            
            {order?.addons?.length > 0 && (
              <div>
                <p className="text-sm text-slate-500">Zusatzoptionen</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {order.addons.map((addon) => (
                    <Badge key={addon} variant="secondary">{addon}</Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Hardware info */}
            <div>
              <p className="text-sm text-slate-500">Hardware</p>
              <p className={`font-medium ${order?.include_router !== false ? 'text-green-600' : 'text-amber-600'}`}>
                {order?.include_router !== false ? '2x FRITZ!Box inklusive' : 'Eigene Hardware'}
              </p>
            </div>
            
            {/* Notes/Special agreements */}
            {order?.notes && (
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-700 mb-1">Anmerkungen/Sondervereinbarungen:</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.notes}</p>
              </div>
            )}
            
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Monatlicher Preis</span>
                <span className="text-xl font-bold text-[#F47920]">
                  {formatCurrency(order?.monthly_total || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-2 text-sm">
                <span className="text-slate-500">Einmalige Kosten</span>
                <span className="font-medium">
                  {order?.setup_fee === 0 ? (
                    <span className="text-green-600">0,00 € (kostenlos)</span>
                  ) : (
                    formatCurrency(order?.setup_fee || 0)
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#F47920]" />
              Anschlussadresse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">
              {order?.customer?.salutation} {order?.customer?.first_name} {order?.customer?.last_name}
            </p>
            <p className="text-slate-600">
              {order?.customer?.service_street} {order?.customer?.service_house_number}
            </p>
            <p className="text-slate-600">
              {order?.customer?.service_postal_code} {order?.customer?.service_city}
            </p>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#F47920]" />
              Vertragsunterlagen
            </CardTitle>
            <CardDescription>
              Bitte lesen Sie alle Dokumente sorgfältig durch
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => openDocument(order?.documents?.agb)}
              data-testid="doc-agb"
            >
              <ExternalLink className="w-4 h-4" />
              Allgemeine Geschäftsbedingungen (AGB)
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => openDocument(order?.documents?.pib)}
              data-testid="doc-pib"
            >
              <ExternalLink className="w-4 h-4" />
              Produktinformationsblatt
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-2 border-[#F47920] text-[#F47920]"
              onClick={() => {
                // Open PDF with auth token
                const pdfUrl = `${API_URL}/customer/vorvertragliche-info`;
                fetch(pdfUrl, {
                  headers: { Authorization: `Bearer ${token}` }
                })
                  .then(res => res.blob())
                  .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');
                  })
                  .catch(() => toast.error('Fehler beim Laden des PDFs'));
              }}
              data-testid="doc-vorvertragliche"
            >
              <Download className="w-4 h-4" />
              Vorvertragliche Informationen (PDF)
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => openDocument(order?.documents?.widerrufsrecht)}
              data-testid="doc-widerrufsrecht"
            >
              <ExternalLink className="w-4 h-4" />
              Widerrufsrecht & Widerrufsformular
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => openDocument(order?.documents?.datenschutzerklaerung)}
              data-testid="doc-datenschutz"
            >
              <ExternalLink className="w-4 h-4" />
              Datenschutzerklärung
            </Button>
          </CardContent>
        </Card>

        {/* Confirmation Section */}
        {step === 'order' && order?.status !== 'confirmed' && (
          <Card className="border-0 shadow-lg border-t-4 border-t-[#F47920]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#F47920]" />
                Bestellung abschließen
              </CardTitle>
              <CardDescription>
                Bitte bestätigen Sie die folgenden Punkte
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="agb"
                    checked={agbAccepted}
                    onCheckedChange={setAgbAccepted}
                    data-testid="checkbox-agb"
                  />
                  <Label htmlFor="agb" className="text-sm cursor-pointer">
                    Ich habe die <a href={order?.documents?.agb} target="_blank" rel="noreferrer" className="text-[#F47920] underline">AGB</a> und die{' '}
                    <a href={order?.documents?.pib} target="_blank" rel="noreferrer" className="text-[#F47920] underline">Leistungsbeschreibung</a> gelesen und akzeptiere diese.
                  </Label>
                </div>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="widerruf"
                    checked={widerrufAccepted}
                    onCheckedChange={setWiderrufAccepted}
                    data-testid="checkbox-widerruf"
                  />
                  <Label htmlFor="widerruf" className="text-sm cursor-pointer">
                    Ich habe die <a href={order?.documents?.widerrufsrecht} target="_blank" rel="noreferrer" className="text-[#F47920] underline">Widerrufsbelehrung</a> zur Kenntnis genommen.
                  </Label>
                </div>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="datenschutz"
                    checked={datenschutzAccepted}
                    onCheckedChange={setDatenschutzAccepted}
                    data-testid="checkbox-datenschutz"
                  />
                  <Label htmlFor="datenschutz" className="text-sm cursor-pointer">
                    Ich habe die <a href={order?.documents?.datenschutzerklaerung} target="_blank" rel="noreferrer" className="text-[#F47920] underline">Datenschutzerklärung</a> gelesen und akzeptiere diese.
                  </Label>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Widerrufsrecht</p>
                    <p>Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleConfirm}
                className="w-full bg-[#F47920] hover:bg-[#E06810] h-12 text-lg"
                disabled={loading || !agbAccepted || !widerrufAccepted || !datenschutzAccepted}
                data-testid="confirm-order-button"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                )}
                Bestellung verbindlich abschließen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 py-4">
          nexiu GmbH - Internet für Alle!
        </p>
      </div>
    </div>
  );
};

export default CustomerPortal;
